<script setup>
import { computed, ref, watch } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, required: true },
  multipleLabel: { type: String, required: true },
  emptyState: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  hasError: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'update:modelValue']);
const { t } = useI18n();
const open = ref(false);
const selectedValues = ref(props.modelValue.map(String));

watch(
  () => props.modelValue,
  values => {
    selectedValues.value = values.map(String);
  }
);

const selectedOptions = computed(() =>
  selectedValues.value.map(value =>
    props.options.find(option => option.value === value)
  )
);
const displayValue = computed(() => {
  if (!selectedValues.value.length) return props.placeholder;
  if (selectedValues.value.length === 1) {
    return selectedOptions.value[0]?.label || selectedValues.value[0];
  }
  return props.multipleLabel.replace(
    '{count}',
    String(selectedValues.value.length)
  );
});
const close = () => {
  if (!open.value) return;
  open.value = false;
  emit('close');
};
const toggle = () => {
  if (props.disabled) return;
  if (open.value) close();
  else open.value = true;
};
const select = value => {
  const next = selectedValues.value.includes(value)
    ? selectedValues.value.filter(item => item !== value)
    : [...selectedValues.value, value];
  selectedValues.value = next;
  emit('update:modelValue', next);
};
const handleKeydown = event => {
  if (event.key === 'Escape') close();
};
</script>

<template>
  <div class="relative w-full" @keydown="handleKeydown">
    <OnClickOutside @trigger="close">
      <button
        type="button"
        class="flex min-h-10 w-full items-center justify-between gap-2 rounded-lais-sm border bg-white px-3 py-2 text-left text-sm transition-colors"
        :class="[
          hasError ? 'border-lais-danger' : 'border-lais-border',
          disabled
            ? 'cursor-not-allowed bg-lais-bg-soft text-lais-text-muted'
            : 'text-lais-text hover:border-lais-primary',
        ]"
        :disabled="disabled"
        :aria-expanded="open"
        aria-haspopup="listbox"
        @click="toggle"
      >
        <span class="truncate">{{ displayValue }}</span>
        <span
          class="i-lucide-chevron-down size-4 shrink-0 text-lais-text-muted"
          aria-hidden="true"
        />
      </button>
      <ul
        v-if="open"
        class="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lais-sm border border-lais-border bg-white py-1 shadow-lais-card"
        role="listbox"
        aria-multiselectable="true"
      >
        <li
          v-for="option in options"
          :key="option.value"
          class="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm text-lais-text hover:bg-lais-bg-soft"
          role="option"
          :aria-selected="selectedValues.includes(option.value)"
          @click="select(option.value)"
        >
          <span>{{ option.label }}</span>
          <span
            v-if="selectedValues.includes(option.value)"
            class="i-lucide-check size-4 text-lais-primary"
            aria-hidden="true"
          />
        </li>
        <li
          v-if="!options.length"
          class="px-3 py-2 text-sm text-lais-text-muted"
        >
          {{ emptyState || t('SALES_PARTIES.CATALOG.DOCUMENT_TYPES_EMPTY') }}
        </li>
      </ul>
    </OnClickOutside>
  </div>
</template>
